import React, { useState, useEffect } from 'react';
import axiosConfig from '../../api/axiosConfig';
import {
  FiSave, FiSettings, FiMail, FiPhone, FiMapPin,
  FiFacebook, FiTwitter, FiInstagram, FiYoutube,
  FiSmartphone, FiEye, FiEyeOff, FiRefreshCw,
  FiGlobe, FiDroplet, FiShield, FiBarChart2
} from 'react-icons/fi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');

const SiteSettingsPage = () => {
  const [settings, setSettings] = useState({
    // Genel Site Bilgileri
    siteName: '',
    siteTitle: '',
    siteDescription: '',
    siteLogo: '',

    // İletişim Bilgileri
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',

    // Sosyal Medya
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    linkedinUrl: '',

    // Mobil Uygulamalar
    appStoreUrl: '',
    googlePlayUrl: '',

    // Harita
    mapLatitude: '',
    mapLongitude: '',
    mapZoom: 15,
    showMap: true,

    // SEO
    metaKeywords: '',
    metaDescription: '',

    // Analitik
    googleAnalyticsId: '',
    facebookPixelId: '',

    // Tema
    primaryColor: '#4F46E5',
    secondaryColor: '#7C3AED',

    // Bakım Modu
    maintenanceMode: false,
    maintenanceMessage: '',

    // Footer
    footerCopyright: '',
    footerText: '',

    // İstatistikler
    studentCount: 10000,
    teacherCount: 500,
    satisfactionRate: 98,

    // Diğer
    allowRegistration: true,
    enableNotifications: true,
    timezone: 'Europe/Istanbul',
    language: 'tr'
  });

  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const tabs = [
    { id: 'general', label: 'Genel Bilgiler', icon: FiSettings },
    { id: 'contact', label: 'İletişim', icon: FiMail },
    { id: 'social', label: 'Sosyal Medya', icon: FiGlobe },
    { id: 'appearance', label: 'Görünüm', icon: FiDroplet },
    { id: 'seo', label: 'SEO & Analitik', icon: FiBarChart2 },
    { id: 'advanced', label: 'Gelişmiş', icon: FiShield }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      // Normal endpoint kullan - yetki kontrolü backend'den kaldırıldı
      const response = await axiosConfig.get('/admin/site-settings');

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Site ayarları yüklenirken hata:', error);
      setMessage({ type: 'error', text: 'Site ayarları yüklenirken hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Yetki kontrolü kaldırıldı
      const response = await axiosConfig.put('/admin/site-settings', settings);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Site ayarları başarıyla kaydedildi!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Site ayarları kaydedilirken hata:', error);
      setMessage({ type: 'error', text: 'Ayarlar kaydedilirken hata oluştu.' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      setMessage({ type: 'info', text: 'Logo yükleniyor...' });

      const response = await axiosConfig.post('/admin/site-settings/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSettings(prev => ({
          ...prev,
          siteLogo: response.data.data.logoUrl
        }));
        setMessage({ type: 'success', text: 'Logo başarıyla yüklendi!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Logo yüklenirken hata:', error);
      setMessage({ type: 'error', text: 'Logo yüklenirken hata oluştu.' });
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      const token = localStorage.getItem('token');
      await axiosConfig.post('/admin/site-settings/maintenance', {
        enabled: !settings.maintenanceMode,
        message: settings.maintenanceMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(prev => ({
        ...prev,
        maintenanceMode: !prev.maintenanceMode
      }));

      setMessage({
        type: 'success',
        text: `Bakım modu ${!settings.maintenanceMode ? 'açıldı' : 'kapatıldı'}!`
      });
    } catch (error) {
      console.error('Bakım modu güncellenirken hata:', error);
      setMessage({ type: 'error', text: 'Bakım modu güncellenirken hata oluştu.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site Adı
          </label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) => handleInputChange('siteName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="EduPlatform"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site Başlığı
          </label>
          <input
            type="text"
            value={settings.siteTitle}
            onChange={(e) => handleInputChange('siteTitle', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Eğitimde Yeni Nesil"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Site Açıklaması
        </label>
        <textarea
          value={settings.siteDescription}
          onChange={(e) => handleInputChange('siteDescription', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="Modern eğitim anlayışıyla..."
        />
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Site Logosu
        </label>
        <div className="flex items-center space-x-4">
          {settings.siteLogo && (
            <div className="flex-shrink-0">
              <img
                src={`${API_BASE_URL}${settings.siteLogo}`}
                alt="Mevcut Logo"
                className="w-16 h-16 object-contain border border-gray-300 rounded-lg"
                onError={(e) => {
                  console.error('Logo yüklenemedi:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <div className="flex-1">
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Logo dosyası seçin (PNG, JPG, GIF, WebP). Maksimum 5MB. Önerilen boyut: 200x60px
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aktif Öğrenci Sayısı
          </label>
          <input
            type="number"
            value={settings.studentCount}
            onChange={(e) => handleInputChange('studentCount', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Uzman Öğretmen Sayısı
          </label>
          <input
            type="number"
            value={settings.teacherCount}
            onChange={(e) => handleInputChange('teacherCount', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Memnuniyet Oranı (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.satisfactionRate}
            onChange={(e) => handleInputChange('satisfactionRate', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiMail className="inline mr-2" />
            E-posta Adresi
          </label>
          <input
            type="email"
            value={settings.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="info@ozelderskurs.com.tr"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiPhone className="inline mr-2" />
            Telefon Numarası
          </label>
          <input
            type="tel"
            value={settings.contactPhone}
            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="+90 (212) 123 45 67"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FiMapPin className="inline mr-2" />
          Adres
        </label>
        <textarea
          value={settings.contactAddress}
          onChange={(e) => handleInputChange('contactAddress', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="İstanbul, Türkiye"
        />
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-4">Harita Ayarları</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enlem (Latitude)
            </label>
            <input
              type="number"
              step="0.000001"
              value={settings.mapLatitude}
              onChange={(e) => handleInputChange('mapLatitude', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="41.0082"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Boylam (Longitude)
            </label>
            <input
              type="number"
              step="0.000001"
              value={settings.mapLongitude}
              onChange={(e) => handleInputChange('mapLongitude', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="28.9784"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zoom Seviyesi
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={settings.mapZoom}
              onChange={(e) => handleInputChange('mapZoom', parseInt(e.target.value) || 15)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.showMap}
              onChange={(e) => handleInputChange('showMap', e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200"
            />
            <span className="ml-2 text-sm text-gray-700">Haritayı göster</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSocialTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiFacebook className="inline mr-2" />
            Facebook URL
          </label>
          <input
            type="url"
            value={settings.facebookUrl}
            onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://facebook.com/username"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiTwitter className="inline mr-2" />
            Twitter URL
          </label>
          <input
            type="url"
            value={settings.twitterUrl}
            onChange={(e) => handleInputChange('twitterUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://twitter.com/username"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiInstagram className="inline mr-2" />
            Instagram URL
          </label>
          <input
            type="url"
            value={settings.instagramUrl}
            onChange={(e) => handleInputChange('instagramUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://instagram.com/username"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiYoutube className="inline mr-2" />
            YouTube URL
          </label>
          <input
            type="url"
            value={settings.youtubeUrl}
            onChange={(e) => handleInputChange('youtubeUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="https://youtube.com/@username"
          />
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-4">
          <FiSmartphone className="inline mr-2" />
          Mobil Uygulama Linkleri
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Store URL
            </label>
            <input
              type="url"
              value={settings.appStoreUrl}
              onChange={(e) => handleInputChange('appStoreUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="https://apps.apple.com/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Play URL
            </label>
            <input
              type="url"
              value={settings.googlePlayUrl}
              onChange={(e) => handleInputChange('googlePlayUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="https://play.google.com/..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ana Renk
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.primaryColor}
              onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={settings.primaryColor}
              onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="#4F46E5"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            İkincil Renk
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.secondaryColor}
              onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={settings.secondaryColor}
              onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="#7C3AED"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Footer Telif Hakkı Metni
        </label>
        <input
          type="text"
          value={settings.footerCopyright}
          onChange={(e) => handleInputChange('footerCopyright', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="© 2024 Özel Ders Kurs Sistemi. Tüm hakları saklıdır."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Footer Açıklama Metni
        </label>
        <textarea
          value={settings.footerText}
          onChange={(e) => handleInputChange('footerText', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="Modern eğitim anlayışıyla binlerce öğrenci ve öğretmeni buluşturan güvenilir platform."
        />
      </div>
    </div>
  );

  const renderSeoTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Anahtar Kelimeler
        </label>
        <textarea
          value={settings.metaKeywords}
          onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="özel ders, eğitim, öğretmen, matematik, fen..."
        />
        <p className="text-sm text-gray-500 mt-1">Virgülle ayırarak yazın</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Açıklama
        </label>
        <textarea
          value={settings.metaDescription}
          onChange={(e) => handleInputChange('metaDescription', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="Modern eğitim anlayışıyla..."
        />
        <p className="text-sm text-gray-500 mt-1">Maksimum 160 karakter önerilir</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Analytics ID
          </label>
          <input
            type="text"
            value={settings.googleAnalyticsId}
            onChange={(e) => handleInputChange('googleAnalyticsId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="GA-XXXXXXXXX"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Facebook Pixel ID
          </label>
          <input
            type="text"
            value={settings.facebookPixelId}
            onChange={(e) => handleInputChange('facebookPixelId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="123456789012345"
          />
        </div>
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
        <h4 className="font-medium text-red-800 mb-3">
          {settings.maintenanceMode ? <FiEyeOff className="inline mr-2" /> : <FiEye className="inline mr-2" />}
          Bakım Modu
        </h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bakım Modu Mesajı
            </label>
            <textarea
              value={settings.maintenanceMessage}
              onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Site bakım modunda. Lütfen daha sonra tekrar deneyiniz."
            />
          </div>
          <button
            onClick={toggleMaintenanceMode}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              settings.maintenanceMode
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {settings.maintenanceMode ? 'Bakım Modunu Kapat' : 'Bakım Modunu Aç'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zaman Dilimi
          </label>
          <select
            value={settings.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="Europe/Istanbul">İstanbul (UTC+3)</option>
            <option value="Europe/London">Londra (UTC+0)</option>
            <option value="America/New_York">New York (UTC-5)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dil
          </label>
          <select
            value={settings.language}
            onChange={(e) => handleInputChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.allowRegistration}
            onChange={(e) => handleInputChange('allowRegistration', e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200"
          />
          <span className="ml-2 text-sm text-gray-700">Yeni kayıtlara izin ver</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.enableNotifications}
            onChange={(e) => handleInputChange('enableNotifications', e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200"
          />
          <span className="ml-2 text-sm text-gray-700">Bildirimleri etkinleştir</span>
        </label>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general': return renderGeneralTab();
      case 'contact': return renderContactTab();
      case 'social': return renderSocialTab();
      case 'appearance': return renderAppearanceTab();
      case 'seo': return renderSeoTab();
      case 'advanced': return renderAdvancedTab();
      default: return renderGeneralTab();
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Site Ayarları</h1>
          <p className="text-gray-600 mt-2">Sitenizin genel ayarlarını yönetin</p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <FiRefreshCw className="animate-spin mr-2" />
              Kaydediliyor...
            </>
          ) : (
            <>
              <FiSave className="mr-2" />
              Kaydet
            </>
          )}
        </button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SiteSettingsPage;
